<script setup lang="ts">
import { ref, reactive, watch, onUnmounted, inject, computed, nextTick } from 'vue'
import { useIwiStore } from '@/stores/iwi'
import { useOpsStore } from '@/stores/ops'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import FosAlertDialog from '@/components/ui/FosAlertDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'

const incidentId = inject<string>('incidentId')!
const iwiStore = useIwiStore()
const opsStore = useOpsStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()

const canResolve = computed(() => permissionsStore.can('iwi.resolve', opRole.value))
const canChecklist = computed(() => permissionsStore.can('iwi.checklist', opRole.value))

const showResolveDialog = ref(false)

// ─── Elapsed timer ────────────────────────────────────────────────────────────

const iwiElapsed = ref('0:00')
let timer: ReturnType<typeof setInterval> | null = null

watch(
  () => iwiStore.activeIwi,
  (iwi) => {
    if (timer) clearInterval(timer)
    timer = null
    if (iwi?.declared_at) {
      timer = setInterval(() => {
        const elapsed = Date.now() - iwi.declared_at.toDate().getTime()
        const totalSecs = Math.floor(elapsed / 1000)
        iwiElapsed.value = `${Math.floor(totalSecs / 60)}:${String(totalSecs % 60).padStart(2, '0')}`
      }, 1000)
    } else {
      iwiElapsed.value = '0:00'
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

// ─── Inline edit state ────────────────────────────────────────────────────────

const TEXT_FIELDS = [
  'chief_complaint',
  'patient_location',
  'point_of_contact',
  'pickup_location',
  'patient_name',
  'patient_age',
  'patient_sex',
  'patient_agency',
  'team_number',
  'emt_name',
] as const

type TextField = typeof TEXT_FIELDS[number]

const fields = reactive<Record<TextField, string>>({
  chief_complaint: '',
  patient_location: '',
  point_of_contact: '',
  pickup_location: '',
  patient_name: '',
  patient_age: '',
  patient_sex: '',
  patient_agency: '',
  team_number: '',
  emt_name: '',
})

const focusedField = ref<string | null>(null)
const editingField = ref<string | null>(null)

watch(
  () => iwiStore.activeIwi,
  (iwi) => {
    if (!iwi) return
    for (const key of TEXT_FIELDS) {
      if (focusedField.value === key) continue
      fields[key] = (iwi as Record<string, unknown>)[key] as string ?? ''
    }
  },
  { immediate: true, deep: true },
)

const inputClass = 'w-full bg-surface-container-high text-on-surface font-mono text-xs px-2 py-1 border border-primary/40 rounded focus:outline-none focus:ring-1 focus:ring-primary/30'

async function startEdit(field: string) {
  editingField.value = field
  focusedField.value = field
  await nextTick()
  const el = document.getElementById(`iwi-field-${field}`)
  el?.focus()
}

function onBlur(field: TextField) {
  focusedField.value = null
  editingField.value = null
  const iwi = iwiStore.activeIwi
  const opId = opsStore.selectedOpId
  if (!iwi || !opId) return
  iwiStore.updateIwiField(incidentId, opId, iwi.iwi_id, field, fields[field] || null)
}

function onSelectChange(field: string, value: string) {
  const iwi = iwiStore.activeIwi
  const opId = opsStore.selectedOpId
  if (!iwi || !opId) return
  editingField.value = null
  if (field === 'severity_life_threatening' || field === 'emt_with_patient') {
    const boolVal = value === 'yes' ? true : value === 'no' ? false : null
    iwiStore.updateIwiField(incidentId, opId, iwi.iwi_id, field, boolVal)
  } else {
    iwiStore.updateIwiField(incidentId, opId, iwi.iwi_id, field, value || null)
  }
}

function displayValue(val: unknown): string {
  if (val === null || val === undefined || val === '') return '—'
  if (val === true) return 'Yes'
  if (val === false) return 'No'
  return String(val)
}

// ─── Checklist ────────────────────────────────────────────────────────────────

const CHECKLIST_STEPS = [
  { key: 'checklist_team_leader_contacted', label: 'Team Leader contacted Comms with patient info' },
  { key: 'checklist_ops_notified', label: 'Operations Chief (or designee) notified' },
  { key: 'checklist_support_requested', label: 'Ground ambulance / helicopter / other support requested' },
  { key: 'checklist_team_leader_is_poc', label: 'Team Leader confirmed as Point of Contact' },
  { key: 'checklist_air_ground_channel', label: 'Pre-assigned Air-to-Ground channel in use' },
  { key: 'checklist_command_channel_cleared', label: 'Command channel cleared for emergency traffic' },
] as const

async function toggleChecklist(item: string) {
  const iwi = iwiStore.activeIwi
  const opId = opsStore.selectedOpId
  if (!iwi || !opId) return
  await iwiStore.toggleChecklist(incidentId, opId, iwi.iwi_id, item)
}

async function resolveIwi() {
  const iwi = iwiStore.activeIwi
  const opId = opsStore.selectedOpId
  if (!iwi || !opId) return
  await iwiStore.resolveIwi(incidentId, opId, iwi.iwi_id)
}

const yesNoOptions = [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]
const transportOptions = [{ label: 'Air', value: 'air' }, { label: 'Ground', value: 'ground' }, { label: 'Self', value: 'self' }]
const genderOptions = [{ label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }, { label: 'Non-Binary', value: 'non-binary' }, { label: 'Other', value: 'other' }]

// Rows: [label, field, type]
// type: 'text' | 'yesno' | 'transport' | 'gender'
const REPORT_ROWS = [
  ['Chief Complaint', 'chief_complaint', 'text'],
  ['Life Threatening', 'severity_life_threatening', 'yesno'],
  ['Patient Location', 'patient_location', 'text'],
  ['Point of Contact', 'point_of_contact', 'text'],
  ['Transport', 'transport', 'transport'],
  ['Pickup Location', 'pickup_location', 'text'],
  ['Patient Name', 'patient_name', 'text'],
  ['Age', 'patient_age', 'text'],
  ['Gender', 'patient_sex', 'gender'],
  ['Patient Agency', 'patient_agency', 'text'],
  ['Team Number', 'team_number', 'text'],
  ['EMT with Patient', 'emt_with_patient', 'yesno'],
  ['EMT Name', 'emt_name', 'text'],
] as const
</script>

<template>
  <div
    v-if="iwiStore.activeIwi"
    class="bg-surface border-2 border-red-500 rounded-lg overflow-hidden iwi-pulse"
  >
    <!-- Header strip -->
    <div class="flex items-center justify-between px-4 py-3 bg-red-600/10 border-b border-red-500/30">
      <div class="flex items-center gap-2.5">
        <span class="w-2.5 h-2.5 rounded-full bg-red-500 iwi-dot-pulse shrink-0" />
        <div>
          <p class="font-headline font-black text-xs uppercase tracking-widest text-red-700 dark:text-red-400">
            IWI — Incident Within Incident
          </p>
          <p class="font-mono text-[0.65rem] text-red-600/80 dark:text-red-500/80 mt-0.5">
            Team <strong>{{ iwiStore.activeIwi.declared_by_team_identifier }}</strong> has declared an emergency
          </p>
        </div>
      </div>
      <span class="font-mono text-3xl font-bold text-red-600 leading-none tabular-nums">{{ iwiElapsed }}</span>
    </div>

    <div class="p-4 grid grid-cols-1 gap-5 md:grid-cols-2">
      <!-- Incident Report fields — click to edit -->
      <div>
        <p class="font-headline font-black text-[0.65rem] uppercase tracking-widest text-outline mb-3">
          Incident Report
        </p>
        <dl class="grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-xs items-center">
          <template v-for="[label, field, type] in REPORT_ROWS" :key="field">
            <dt class="text-outline uppercase tracking-wider text-[0.6rem] self-center">{{ label }}</dt>
            <dd class="min-w-0">
              <!-- Text fields -->
              <template v-if="type === 'text'">
                <input
                  v-if="editingField === field"
                  :id="`iwi-field-${field}`"
                  v-model="fields[field as TextField]"
                  :class="inputClass"
                  @blur="onBlur(field as TextField)"
                  @keydown.enter="onBlur(field as TextField)"
                  @keydown.escape="editingField = null; focusedField = null"
                />
                <button
                  v-else
                  class="text-left w-full truncate px-1 py-0.5 rounded hover:bg-surface-container-low transition-colors"
                  :class="(iwiStore.activeIwi as Record<string, unknown>)[field] ? 'text-on-surface-variant' : 'text-outline/50 italic'"
                  @click="startEdit(field)"
                >
                  {{ displayValue((iwiStore.activeIwi as Record<string, unknown>)[field]) }}
                </button>
              </template>

              <!-- Boolean (yes/no) select -->
              <template v-else-if="type === 'yesno'">
                <FosSelect
                  v-if="editingField === field"
                  :model-value="(iwiStore.activeIwi as Record<string, unknown>)[field] === true ? 'yes' : (iwiStore.activeIwi as Record<string, unknown>)[field] === false ? 'no' : undefined"
                  :options="yesNoOptions"
                  placeholder="—"
                  @update:model-value="onSelectChange(field, $event)"
                />
                <button
                  v-else
                  class="text-left w-full truncate px-1 py-0.5 rounded hover:bg-surface-container-low transition-colors"
                  :class="(iwiStore.activeIwi as Record<string, unknown>)[field] !== null && (iwiStore.activeIwi as Record<string, unknown>)[field] !== undefined ? 'text-on-surface-variant' : 'text-outline/50 italic'"
                  @click="startEdit(field)"
                >
                  {{ displayValue((iwiStore.activeIwi as Record<string, unknown>)[field]) }}
                </button>
              </template>

              <!-- Gender select -->
              <template v-else-if="type === 'gender'">
                <FosSelect
                  v-if="editingField === field"
                  :model-value="(iwiStore.activeIwi as Record<string, unknown>)[field] as string ?? undefined"
                  :options="genderOptions"
                  placeholder="—"
                  @update:model-value="onSelectChange(field, $event)"
                />
                <button
                  v-else
                  class="text-left w-full truncate px-1 py-0.5 rounded hover:bg-surface-container-low transition-colors"
                  :class="(iwiStore.activeIwi as Record<string, unknown>)[field] ? 'text-on-surface-variant' : 'text-outline/50 italic'"
                  @click="startEdit(field)"
                >
                  {{ displayValue((iwiStore.activeIwi as Record<string, unknown>)[field]) }}
                </button>
              </template>

              <!-- Transport select -->
              <template v-else-if="type === 'transport'">
                <FosSelect
                  v-if="editingField === field"
                  :model-value="(iwiStore.activeIwi as Record<string, unknown>)[field] as string ?? undefined"
                  :options="transportOptions"
                  placeholder="—"
                  @update:model-value="onSelectChange(field, $event)"
                />
                <button
                  v-else
                  class="text-left w-full truncate px-1 py-0.5 rounded hover:bg-surface-container-low transition-colors"
                  :class="(iwiStore.activeIwi as Record<string, unknown>)[field] ? 'text-on-surface-variant' : 'text-outline/50 italic'"
                  @click="startEdit(field)"
                >
                  {{ displayValue((iwiStore.activeIwi as Record<string, unknown>)[field]) }}
                </button>
              </template>
            </dd>
          </template>
        </dl>
      </div>

      <!-- Response checklist + resolve -->
      <div class="flex flex-col gap-4">
        <div>
          <p class="font-headline font-black text-[0.65rem] uppercase tracking-widest text-outline mb-3">
            Response Checklist
          </p>
          <ul class="flex flex-col gap-2">
            <li
              v-for="step in CHECKLIST_STEPS"
              :key="step.key"
              class="flex items-start gap-2.5"
            >
              <button
                :disabled="!canChecklist"
                class="mt-0.5 w-4 h-4 shrink-0 border rounded flex items-center justify-center transition-colors focus:outline-none focus:ring-1 focus:ring-primary/30"
                :class="[
                  (iwiStore.activeIwi as Record<string, unknown>)[step.key]
                    ? 'bg-status-on-scene border-status-on-scene text-white'
                    : 'bg-surface-container-high border-border',
                  canChecklist ? 'cursor-pointer hover:border-primary' : 'cursor-default opacity-60',
                ]"
                @click="toggleChecklist(step.key)"
              >
                <span
                  v-if="(iwiStore.activeIwi as Record<string, unknown>)[step.key]"
                  class="material-symbols-outlined text-[12px] leading-none"
                >
                  check
                </span>
              </button>
              <span
                class="font-mono text-xs leading-snug"
                :class="(iwiStore.activeIwi as Record<string, unknown>)[step.key] ? 'text-outline line-through' : 'text-on-surface-variant'"
              >
                {{ step.label }}
              </span>
            </li>
          </ul>
        </div>

        <!-- Resolve button -->
        <div class="pt-2 border-t border-border">
          <PermissionGuard action="iwi.resolve" :role="opRole">
            <FosButton
              variant="secondary"
              class="w-full"
              @click="showResolveDialog = true"
            >
              IWI Resolved
            </FosButton>
          </PermissionGuard>
        </div>
      </div>
    </div>
  </div>

  <!-- Resolve confirmation dialog -->
  <FosAlertDialog
    :open="showResolveDialog"
    title="Resolve IWI?"
    description="This marks the incident as resolved. The IWI record will be preserved for the audit log. The declaring team's status must be updated manually."
    confirm-label="Resolve"
    cancel-label="Cancel"
    @update:open="showResolveDialog = $event"
    @confirm="resolveIwi"
  />
</template>

<style scoped>
.iwi-pulse {
  animation: iwi-border-pulse 1.2s ease-in-out infinite;
}

@keyframes iwi-border-pulse {
  0%, 100% {
    border-color: rgb(239 68 68);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
  50% {
    border-color: rgb(248 113 113);
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
  }
}

.iwi-dot-pulse {
  animation: dot-pulse 1.2s ease-in-out infinite;
}

@keyframes dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.75); }
}
</style>
